/**
 * 로거·콘솔 제어
 *
 * CRA `npm run build`는 항상 NODE_ENV=production 이라, 브라우저 로그 출력 여부는
 * REACT_APP_ENABLE_LOGS 플래그를 우선 기준으로 삼는다.
 */

const isWebpackProductionBundle = process.env.NODE_ENV === "production";
const isDevelopment = process.env.NODE_ENV === "development";
const ENABLE_LOGS_RAW = process.env.REACT_APP_ENABLE_LOGS;
const hasExplicitLogFlag = typeof ENABLE_LOGS_RAW === "string";
const enableLogsFromEnv = ENABLE_LOGS_RAW === "true";
const DEPLOY_TIER_RAW = process.env.REACT_APP_DEPLOY_TIER ?? "";
const DEPLOY_TIER = DEPLOY_TIER_RAW.toLowerCase();

/** 일반 사용자용 Firebase Hosting(prod) 빌드만 true. dev/stage는 false. */
const isProductionDeploy =
  DEPLOY_TIER === "production" ||
  (DEPLOY_TIER === "" && isWebpackProductionBundle);

const apiUrl = process.env.REACT_APP_API_URL ?? "";
const isStageOrDevApi =
  /viewrapi-(?:dev|stg|stage)\b|[-.](?:dev|stg|stage)\./i.test(apiUrl);
const isLocalhostRuntime =
  typeof window !== "undefined" &&
  /(^localhost$|^127\.0\.0\.1$|\.local$)/i.test(window.location.hostname);

const runtimeAlwaysEnablesLogs =
  isDevelopment ||
  isLocalhostRuntime;
const shouldEnableLogs =
  runtimeAlwaysEnablesLogs ||
  (hasExplicitLogFlag
    ? enableLogsFromEnv
    : !isProductionDeploy && isStageOrDevApi);

/** 하위 호환: 기존 isProduction 이름 유지 (번들이 production 빌드인지) */
const isProduction = isWebpackProductionBundle;

/**
 * Unity `public/unity/index.html` 은 console 을 열 때 `debugConsole=1` 쿼리가 필요하다.
 */
export function getUnityIframeConsoleQueryString(): string {
  return shouldEnableLogs ? "&debugConsole=1" : "";
}

declare global {
  interface Window {
    __VIEWR_CONSOLE_SILENCED__?: boolean;
    __VIEWR_ORIGINAL_CONSOLE__?: Record<string, unknown>;
  }
}

const CONSOLE_METHODS = [
  "log",
  "info",
  "debug",
  "warn",
  "error",
  "trace",
  "group",
  "groupCollapsed",
  "groupEnd",
  "table",
  "dir",
  "time",
  "timeLog",
  "timeEnd",
  "assert",
  "clear",
] as const;

export const silenceConsoleInProduction = (): void => {
  if (
    typeof window === "undefined" ||
    shouldEnableLogs ||
    window.__VIEWR_CONSOLE_SILENCED__
  ) {
    return;
  }

  const originalConsole = window.__VIEWR_ORIGINAL_CONSOLE__ ?? {};
  const noop = () => {};
  const consoleRef = console as unknown as Record<string, unknown>;

  CONSOLE_METHODS.forEach((method) => {
    const currentMethod = consoleRef[method];
    if (typeof currentMethod === "function") {
      originalConsole[method] = (currentMethod as (...args: unknown[]) => void).bind(
        console
      );
    }
    consoleRef[method] = noop;
  });

  window.__VIEWR_ORIGINAL_CONSOLE__ = originalConsole;
  window.__VIEWR_CONSOLE_SILENCED__ = true;
};

/**
 * 일반 로그 (프로덕션에서 비활성화)
 */
export const log = (...args: unknown[]): void => {
  if (shouldEnableLogs) {
    console.log(...args);
  }
};

/**
 * 경고 로그 (프로덕션에서 비활성화)
 */
export const warn = (...args: unknown[]): void => {
  if (shouldEnableLogs) {
    console.warn(...args);
  }
};

/**
 * 에러 로그 (프로덕션에서 비활성화)
 */
export const error = (...args: unknown[]): void => {
  if (shouldEnableLogs) {
    console.error(...args);
  }
};

/**
 * 정보 로그 (프로덕션에서 비활성화)
 */
export const info = (...args: unknown[]): void => {
  if (shouldEnableLogs) {
    console.info(...args);
  }
};

/**
 * 디버그 로그 (로컬 dev 서버 또는 dev/stage 배포에서 표시)
 */
export const debug = (...args: unknown[]): void => {
  if (shouldEnableLogs) {
    console.debug(...args);
  }
};

/**
 * 조건부 로그 (특정 조건에서만 로그 출력)
 */
export const conditionalLog = (
  condition: boolean,
  ...args: unknown[]
): void => {
  if (condition && shouldEnableLogs) {
    console.log(...args);
  }
};

/**
 * 그룹 로그 (프로덕션에서 비활성화)
 */
export const group = (label: string): void => {
  if (shouldEnableLogs) {
    console.group(label);
  }
};

export const groupEnd = (): void => {
  if (shouldEnableLogs) {
    console.groupEnd();
  }
};

/**
 * 테이블 로그 (프로덕션에서 비활성화)
 */
export const table = (data: unknown): void => {
  if (shouldEnableLogs) {
    console.table(data);
  }
};

/**
 * 현재 로그 설정 상태 반환
 */
export const getLogConfig = () => ({
  isProduction,
  isDevelopment,
  logsEnabled: shouldEnableLogs,
  deployTier: DEPLOY_TIER_RAW || undefined,
  isProductionDeploy,
});
